import { Content, Slice } from '@prismicio/client';
import { ProductCatalogService } from '@yol-digital/ms-client';
import { Image } from 'interfaces';
import {
  convertProductBoxTable,
  prismicToHtmlString,
  prismicToImage,
  ProductBoxDocumentDataBodyFeaturesSlice,
  ProductBoxFeatures,
} from 'prismic';
import { urlify } from 'utils';
type CatalogResponse = ProductCatalogService.CatalogResponse;

export class Product {
  constructor(
    rateplan: CatalogResponse,
    prismicData?: Partial<Content.ProductBoxDocumentData>,
    channelId?: string,
    selfcare?: boolean
  ) {
    this.id = rateplan.id;
    this.product_code = rateplan.code;
    this.source = rateplan.source;
    this.name = prismicData?.display_name
      ? prismicData?.display_name
      : rateplan.name.replace(/yallo|lebara|chmobile/g, '');
    this.url = urlify(rateplan.displayName);
    this.monthly_cost = rateplan.basePrice;
    this.activation_fee = rateplan.activationFee;
    this.rateplan_id = rateplan.id;
    this.lockin_length = rateplan.availableLockings;
    this.defaultPromotion = rateplan.defaultPromotion;
    this.productSpecFamily = rateplan.productSpecFamily;
    this.product_type =
      prismicData?.product_type?.toLocaleLowerCase() ?? rateplan.productSpecFamily.toLocaleLowerCase();
    this.productSpecClass = rateplan.productSpecClass;
    this.isSlave = rateplan.isSlave;
    this;
    //Get values from Prismic
    this.hasPrismicData = !!prismicData && Object.keys(prismicData).length !== 0;
    if (prismicData) {
      this.short_description = prismicToHtmlString(prismicData.short_description);
      if (prismicData.product_icon?.url) {
        this.product_icon = prismicToImage(prismicData.product_icon);
      }
      this.productSpecSubCategory = prismicData.product_spec_sub_category;
      this.product_details_link_label = prismicData.product_details_link_label;
      this.product_details_link = prismicData.product_details_link;
      this.cta_button_label = prismicData.cta_button_label;
      this.alternative_link = prismicData.alternative_link;
      this.special_product_type = prismicData.special_product_type;
      this.promotion_banner_text = prismicData.promotion_banner_text;
      this.checkout_basket_additional_info = prismicToHtmlString(prismicData.checkout_basket_additional_info);
      if (prismicData.body) {
        this.prismicTable = convertProductBoxTable(
          prismicData.body.filter(
            (slice: Slice) => slice.slice_type === 'features'
          ) as ProductBoxDocumentDataBodyFeaturesSlice[]
        );
      }

      //A product from prismic can have multiple channels, so only add lockinLength if there's a channelId
      if (channelId && this.hasPrismicData) {
        const channelItem = prismicData.channels.find((d: { channel: { id: string } }) => d.channel.id === channelId);
        if (channelItem) {
          if (channelItem['0_month_lockin']) this.lockin_length.push(0);
          if (channelItem['12_month_lockin']) this.lockin_length.push(12);
          if (channelItem['24_month_lockin']) this.lockin_length.push(24);
        }
      }
    }

    this.type = this.productSpecClass === 'PREPAID' ? 'prepaid' : 'postpaid';

    this.setAnalyticsCategories(rateplan.productSpecClass, rateplan.productSpecFamily, selfcare);
  }

  public id: string;
  public hasPrismicData: boolean;
  public source: CatalogResponse['source'];
  public name: string;
  public short_description: string;
  public url: string;
  public monthly_cost: Content.ProductBoxDocumentData['monthly_cost'] | CatalogResponse['basePrice'];
  public activation_fee?: CatalogResponse['activationFee'];
  public rateplan_id: Content.ProductBoxDocumentData['rateplan_id'] | CatalogResponse['id'];
  public type: Lowercase<Content.ProductBoxDocumentData['type']> | string;
  //TODO: Remove mbb and fiber when the content team migrate to product box kitsune
  public product_type: Lowercase<Content.ProductBoxDocumentData['product_type']> | CatalogResponse['productSpecFamily'];
  public lockin_length: number[] = []; // Note, this can be different depending on the channel
  public product_icon: Image;
  public product_details_link: Content.ProductBoxDocumentData['product_details_link'];
  public product_details_link_label: Content.ProductBoxDocumentData['product_details_link_label'];
  public cta_button_label: Content.ProductBoxDocumentData['cta_button_label'];
  public alternative_link: Content.ProductBoxDocumentData['alternative_link'];
  public promotion_banner_text: Content.ProductBoxDocumentData['promotion_banner_text'];
  public special_product_type: Content.ProductBoxDocumentData['special_product_type'];
  public product_code: Content.ProductBoxDocumentData['product_code'] | CatalogResponse['code'];
  public isSlave: CatalogResponse['isSlave'];
  public productSpecFamily: CatalogResponse['productSpecFamily'];
  public productSpecSubCategory: Content.ProductBoxDocumentData['product_spec_sub_category'];
  public productSpecClass: CatalogResponse['productSpecClass'];
  public defaultPromotion?: CatalogResponse['defaultPromotion'];
  public prismicTable?: ProductBoxFeatures[];
  public item_category?: 'Mobile abo' | 'TV' | 'Prepaid' | 'Fixline' | 'Activation fee';
  public item_category2?: 'Mobile voice' | 'TV' | 'Prepaid' | 'Mobile data' | 'Internet' | 'Activation fee';
  public item_category3?:
    | 'Mobile voice'
    | 'TV'
    | 'Prepaid SIM'
    | 'Internet on the Go'
    | 'Internet at Home'
    | 'Activation fee';
  public item_category4?: 'Activation fee' | 'not selfcare' | 'selfcare';
  public item_category5?: 'Activation fee' | 'not selfcare' | 'selfcare';
  public item_variant?:
    | 'postpaid mobile'
    | 'tv subscription'
    | 'prepaid mobile'
    | 'postpaid mbb'
    | 'postpaid cable'
    | 'postpaid fiber'
    | 'service fees';
  public checkout_basket_additional_info: string;

  public discountedPrice(discount?: number): number {
    const cost = +this.monthly_cost;
    if (!cost || isNaN(cost) || !discount) {
      return this.monthly_cost;
    }

    return Number((cost - discount).toFixed(2));
  }

  public setAnalyticsCategories(productSpecClass: string, productSpecFamily: string, selfcare?: boolean) {
    switch (productSpecClass?.toUpperCase() + '/' + productSpecFamily?.toUpperCase()) {
      case 'POSTPAID/MOBILE':
        this.item_category = 'Mobile abo';
        this.item_category2 = 'Mobile voice';
        this.item_category3 = 'Mobile voice';
        this.item_category4 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_category5 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_variant = 'postpaid mobile';
        break;
      case 'TV/TV':
        this.item_category = 'TV';
        this.item_category2 = 'TV';
        this.item_category3 = 'TV';
        this.item_category4 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_category5 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_variant = 'tv subscription';
        break;
      case 'PREPAID/MOBILE':
        this.item_category = 'Prepaid';
        this.item_category2 = 'Prepaid';
        this.item_category3 = 'Prepaid SIM';
        this.item_category4 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_category5 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_variant = 'prepaid mobile';
        break;
      case 'POSTPAID/INTERNET':
        this.item_category = 'Mobile abo';
        this.item_category2 = 'Mobile data';
        this.item_category3 = 'Internet on the Go';
        this.item_category4 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_category5 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_variant = 'postpaid mbb';
        break;
      case 'MBB/INTERNET':
        this.item_category = 'Mobile abo';
        this.item_category2 = 'Mobile data';
        this.item_category3 = 'Internet at Home';
        this.item_category4 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_category5 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_variant = 'postpaid mbb';
        break;
      case 'HFC/INTERNET':
        this.item_category = 'Fixline';
        this.item_category2 = 'Internet';
        this.item_category3 = 'Internet at Home';
        this.item_category4 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_category5 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_variant = 'postpaid cable';
        break;
      case 'FIBER/INTERNET':
        this.item_category = 'Fixline';
        this.item_category2 = 'Internet';
        this.item_category3 = 'Internet at Home';
        this.item_category4 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_category5 = selfcare ? 'selfcare' : 'not selfcare';
        this.item_variant = 'postpaid fiber';
        break;
    }
  }
}
